import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  TreePine, MessagesSquare, CalendarHeart,
  Images, Sparkles, ShieldCheck, ArrowRight, Check,
} from "lucide-react";
import logoMark from "../assets/logo-mark.png";

const HERO_IMGS = [
  "https://images.unsplash.com/photo-1613591876822-846e82526ee7?fit=facearea&facepad=8&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBibGFjayUyMGZhbWlseSUyMHN0dWRpbyUyMHBvcnRyYWl0fGVufDB8fHx8MTc4Nzk0MTM3MHww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1611024847487-e26177381a3f?fit=facearea&facepad=8&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxsYXRpbm8lMjBoaXNwYW5pYyUyMGZhbWlseSUyMHBvcnRyYWl0JTIwYnJpZ2h0fGVufDB8fHx8MTc4Nzk0MTM3MHww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1707454301491-3ae3acea8aa7?fit=facearea&facepad=8&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwzfHxoYXBweSUyMGFzaWFuJTIwZmFtaWx5JTIwcG9ydHJhaXQlMjBtb2Rlcm58ZW58MHx8fHwxNzg3OTQxMzcwfDA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1758687125973-ec9932a4245b?fit=facearea&facepad=8&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwzfHxiaXJhY2lhbCUyMGZhbWlseSUyMGJyaWdodCUyMG1vZGVybiUyMGhvbWUlMjBsaWZlc3R5bGV8ZW58MHx8fHwxNzg3OTQxOTYzfDA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1666819256222-7034f91340a7?fit=facearea&facepad=8&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxtdWx0aWdlbmVyYXRpb25hbCUyMGZhbWlseSUyMHBvcnRyYWl0JTIwY29udGVtcG9yYXJ5fGVufDB8fHx8MTc4Nzk0MTM3MHww&ixlib=rb-4.1.0&q=85",
];

const FEATURES = [
  { icon: TreePine, title: "Lineage", desc: "An editable family tree that keeps every branch, story and name in one living record." },
  { icon: MessagesSquare, title: "Hangouts", desc: "A private community feed with micro-threads and polls so the family always stays in the loop." },
  { icon: CalendarHeart, title: "Reunions", desc: "Plan gatherings, post announcements and collect RSVPs with secure checkout — all in one place." },
  { icon: Images, title: "Memories", desc: "A timeless feed of stories, recipes and moments, sorted the way your family lives them." },
  { icon: Sparkles, title: "The Vault", desc: "AI helpers for heritage, event planning, games and family business — private to your family." },
  { icon: ShieldCheck, title: "Private by design", desc: "One family per vault. Your data stays isolated, secure, and yours alone." },
];

const TIERS = [
  { name: "Roots", price: "Free", tag: "Start here", perks: ["Family tree & lineage", "Hangouts feed & polls", "Shared memories", "10 AI credits / month"], highlight: false },
  { name: "Branches", price: "$6.99", per: "/mo", tag: "Most loved", perks: ["Everything in Roots", "Reunions with RSVP checkout", "The Vault AI helpers", "100 AI credits / month", "More storage"], highlight: true },
  { name: "Legacy", price: "$14.99", per: "/mo", tag: "Full legacy", perks: ["Everything in Branches", "Priority AI & storage", "300 AI credits / month", "Business hub tools", "Legacy preservation suite"], highlight: false },
];

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55, delay: d },
});

export default function Marketing() {
  const nav = useNavigate();
  const [heroIdx, setHeroIdx] = useState(0);
  useEffect(() => { document.title = "The Family Vault — one private home for your family"; }, []);
  useEffect(() => {
    const t = setInterval(() => setHeroIdx((i) => (i + 1) % HERO_IMGS.length), 4000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="min-h-screen w-full grain relative overflow-x-hidden" style={{ background: "var(--bg-base)" }} data-testid="marketing-landing">
      {/* Nav */}
      <header className="sticky top-0 z-50 glass" style={{ borderBottom: "1px solid var(--border)", paddingTop: "env(safe-area-inset-top)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl grid place-items-center gold-border" style={{ background: "var(--bg-elevated)" }}>
              <img src={logoMark} alt="The Family Vault" className="h-6 w-6 object-contain" />
            </div>
            <span className="font-serif-display text-xl">The Family Vault</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <button data-testid="marketing-signin-top" onClick={() => nav("/login")}
              className="text-sm font-medium px-4 py-2 rounded-full" style={{ color: "var(--text-secondary)" }}>
              Sign in
            </button>
            <button data-testid="marketing-get-started-top" onClick={() => nav("/login?mode=register")}
              className="text-sm font-semibold px-4 py-2 rounded-full" style={{ background: "var(--accent)", color: "var(--bg-base)" }}>
              Get started
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-16 lg:grid lg:grid-cols-2 lg:gap-16 lg:items-center">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full mb-6"
            style={{ background: "var(--overlay)", color: "var(--text-secondary)" }}>
            <ShieldCheck size={13} className="gold-text" /> Private by design · one family per vault
          </div>
          <h1 className="font-serif-display text-5xl lg:text-6xl xl:text-7xl leading-[1.03] tracking-tight">
            One private home for your family to <span className="gold-text">connect, plan & preserve</span>.
          </h1>
          <p className="mt-6 text-base lg:text-lg max-w-xl" style={{ color: "var(--text-secondary)" }}>
            Bring lineage, gatherings, memories and everyday chatter together in a beautiful space that belongs only to your family.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3 sm:max-w-lg">
            <button data-testid="marketing-get-started" onClick={() => nav("/login?mode=register")}
              className="rounded-2xl px-7 py-4 font-semibold text-sm flex items-center justify-center gap-2 flex-1"
              style={{ background: "var(--accent)", color: "var(--bg-base)" }}>
              Create your family vault <ArrowRight size={16} />
            </button>
            <button data-testid="marketing-signin" onClick={() => nav("/login")}
              className="rounded-2xl px-7 py-4 font-semibold text-sm gold-border flex-1"
              style={{ color: "var(--text-primary)", background: "var(--bg-surface)" }}>
              I already have an account
            </button>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.1 }}
          className="block relative mt-10 lg:mt-0">
          <div className="relative rounded-3xl overflow-hidden" style={{ border: "1px solid var(--border)", boxShadow: "0 30px 80px rgba(0,0,0,0.5)" }} data-testid="marketing-hero-carousel">
            <div className="relative w-full h-[340px] sm:h-[420px] lg:h-[560px]">
              {HERO_IMGS.map((src, i) => (
                <motion.img
                  key={src}
                  src={src}
                  alt="A family"
                  data-testid={`marketing-hero-img-${i}`}
                  initial={false}
                  animate={{ opacity: heroIdx === i ? 1 : 0 }}
                  transition={{ duration: 1.1, ease: "easeInOut" }}
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ objectPosition: "center 30%" }}
                />
              ))}
            </div>
            <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, transparent 55%, rgba(11,11,18,0.7))" }} />
            <div className="absolute bottom-4 left-4 glass rounded-xl px-4 py-2.5 max-w-[70%]" style={{ border: "1px solid var(--border)" }}>
              <div className="font-serif-display text-base sm:text-lg gold-text leading-tight">Every branch. One vault.</div>
              <p className="text-[11px] mt-0.5 leading-snug" style={{ color: "var(--text-secondary)" }}>From your great-grandparents to the newest arrival.</p>
            </div>
            <div className="absolute top-5 right-5 flex gap-1.5">
              {HERO_IMGS.map((_, i) => (
                <button key={i} onClick={() => setHeroIdx(i)} data-testid={`marketing-hero-dot-${i}`}
                  aria-label={`Show family photo ${i + 1}`}
                  className="h-2 rounded-full transition-all"
                  style={{ width: heroIdx === i ? 20 : 8, background: heroIdx === i ? "var(--accent)" : "rgba(255,255,255,0.5)" }} />
              ))}
            </div>
          </div>
        </motion.div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <motion.div {...fade(0)} className="max-w-2xl">
          <h2 className="font-serif-display text-4xl lg:text-5xl leading-tight tracking-tight">
            Everything your family needs, in one vault
          </h2>
          <p className="mt-4 text-base lg:text-lg" style={{ color: "var(--text-secondary)" }}>
            Thoughtfully designed spaces for the way real families stay close.
          </p>
        </motion.div>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, i) => (
            <motion.div key={f.title} {...fade((i % 3) * 0.08)}
              whileHover={{ y: -6 }}
              className="card-surface rounded-3xl p-7" data-testid={`marketing-feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="h-12 w-12 rounded-2xl grid place-items-center gold-border mb-5"
                style={{ background: "var(--bg-elevated)" }}>
                <f.icon size={22} className="gold-text" />
              </div>
              <div className="font-serif-display text-2xl">{f.title}</div>
              <p className="text-sm mt-2 leading-relaxed" style={{ color: "var(--text-secondary)" }}>{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <motion.div {...fade(0)} className="max-w-2xl">
          <h2 className="font-serif-display text-4xl lg:text-5xl leading-tight tracking-tight">
            Simple plans that grow with the family
          </h2>
          <p className="mt-4 text-base lg:text-lg" style={{ color: "var(--text-secondary)" }}>
            Start free. Upgrade whenever your family is ready for more.
          </p>
        </motion.div>
        <div className="mt-12 grid gap-5 lg:grid-cols-3">
          {TIERS.map((t, i) => (
            <motion.div key={t.name} {...fade(i * 0.08)}
              whileHover={{ y: -6 }}
              className="rounded-3xl p-8"
              data-testid={`marketing-tier-${t.name.toLowerCase()}`}
              style={t.highlight
                ? { background: "var(--bg-elevated)", border: "1px solid color-mix(in srgb, var(--accent) 45%, transparent)", boxShadow: "0 0 48px var(--shell-glow)" }
                : { background: "var(--bg-surface)", border: "1px solid var(--hairline)" }}>
              <div className="flex items-center justify-between">
                <div className="font-serif-display text-3xl">{t.name}</div>
                <span className="text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full"
                  style={{ background: "var(--overlay)", color: t.highlight ? "var(--accent)" : "var(--text-secondary)" }}>
                  {t.tag}
                </span>
              </div>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-4xl font-bold">{t.price}</span>
                {t.per && <span className="text-sm" style={{ color: "var(--text-secondary)" }}>{t.per}</span>}
              </div>
              <ul className="mt-6 space-y-3">
                {t.perks.map((p) => (
                  <li key={p} className="flex items-center gap-2.5 text-sm" style={{ color: "var(--text-secondary)" }}>
                    <Check size={16} className="gold-text shrink-0" /> {p}
                  </li>
                ))}
              </ul>
              <button data-testid={`marketing-tier-cta-${t.name.toLowerCase()}`} onClick={() => nav("/login?mode=register")}
                className="mt-8 w-full rounded-2xl py-3.5 font-semibold text-sm"
                style={t.highlight
                  ? { background: "var(--accent)", color: "var(--bg-base)" }
                  : { background: "var(--overlay)", color: "var(--text-primary)", border: "1px solid var(--border)" }}>
                Get started
              </button>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <motion.div {...fade(0)} className="rounded-[2rem] p-10 lg:p-16 text-center relative overflow-hidden"
          style={{ background: "var(--bg-elevated)", border: "1px solid var(--hairline)" }}>
          <div className="h-16 w-16 mx-auto rounded-2xl grid place-items-center gold-border mb-6"
            style={{ background: "var(--bg-surface)", boxShadow: "0 0 24px var(--shell-glow)" }}>
            <img src={logoMark} alt="The Family Vault" className="h-11 w-11 object-contain" />
          </div>
          <h3 className="font-serif-display text-4xl lg:text-5xl leading-tight max-w-2xl mx-auto">Give your family a place of their own.</h3>
          <button data-testid="marketing-cta-bottom" onClick={() => nav("/login?mode=register")}
            className="mt-8 inline-flex items-center justify-center gap-2 rounded-2xl px-8 py-4 font-semibold text-sm"
            style={{ background: "var(--accent)", color: "var(--bg-base)" }}>
            Get started free <ArrowRight size={16} />
          </button>
        </motion.div>
        <div className="mt-10 flex flex-col items-center gap-3">
          <button data-testid="marketing-legal-shield" onClick={() => nav("/legal")}
            className="group h-12 w-12 rounded-full grid place-items-center transition-all"
            style={{ background: "var(--bg-surface)", border: "1px solid var(--hairline)" }}
            title="Privacy Policy & Terms of Use" aria-label="Privacy Policy and Terms of Use">
            <ShieldCheck size={20} className="gold-text transition-transform group-hover:scale-110" />
          </button>
          <button onClick={() => nav("/legal")} data-testid="marketing-legal-link"
            className="text-xs underline-offset-4 hover:underline" style={{ color: "var(--text-muted)" }}>
            Privacy Policy &amp; Terms of Use
          </button>
          <p className="text-center text-xs" style={{ color: "var(--text-muted)" }}>
            © {new Date().getFullYear()} The Family Vault
          </p>
        </div>
      </section>
    </div>
  );
}
